import { FC } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  formattedCurrency,
  withStyles,
  GooglePayChargeTransactionResponse,
  PayPalWithdrawTransactionResponse,
  VenmoWithdrawTransactionResponse,
  Card,
  TransactionType,
} from 'vopay';
import { TransactionCard, Title, DetailsRows } from 'UI';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';
import { useClientData } from '@/providers/ClientData.provider';

const cn = withStyles(styles);

type DigitalWalletTransaction =
  | GooglePayChargeTransactionResponse
  | PayPalWithdrawTransactionResponse
  | VenmoWithdrawTransactionResponse;

type DigitalWalletTransactionInfoProps = {
  transaction: DigitalWalletTransaction;
  transactionType: 'GooglePay' | 'PayPal' | 'Venmo';
  fullName: string;
  jwt: string;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
  accountId: string;
};

const DigitalWalletTransactionInfo: FC<DigitalWalletTransactionInfoProps> = ({
  transaction,
  transactionType,
  fullName,
  jwt,
}) => {
  const { state } = useLocation<DataProps>();

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.DigitalWalletTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const {
    response: { JWT: mainJwt },
  } = useClientData();
  const isMainAccount = !jwt || jwt === mainJwt;

  // Helper function to map transaction type to wallet display name
  const getWalletDisplayName = (type: 'GooglePay' | 'PayPal' | 'Venmo'): string => {
    const walletTypeMap: Record<typeof type, string> = {
      GooglePay: t('googlePay', { defaultValue: 'Google Pay' }),
      PayPal: t('paypal', { defaultValue: 'PayPal' }),
      Venmo: t('venmo', { defaultValue: 'Venmo' }),
    };
    return walletTypeMap[type];
  };

  // Helper function to get wallet-specific token field
  const getWalletToken = () => {
    if (transactionType === 'GooglePay' && 'GooglePayToken' in transaction) {
      return transaction.GooglePayToken;
    } else if (transactionType === 'PayPal' && 'PayPalToken' in transaction) {
      return transaction.PayPalToken;
    } else if (transactionType === 'Venmo' && 'VenmoToken' in transaction) {
      return transaction.VenmoToken;
    }
    return '';
  };

  const walletType =
    transactionType === 'GooglePay'
      ? TransactionType.GOOGLE_PAY
      : transactionType === 'Venmo'
        ? TransactionType.VENMO_WITHDRAWAL
        : TransactionType.PAYPAL_WITHDRAWAL;

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionDateTime', { defaultValue: 'Transaction Date Time' }),
      value: moment(transaction.TransactionDateTime).format('YYYY-MM-DD HH:mm:ss') || '-',
    },
    {
      label: t('TransactionDetails.transactionType', { defaultValue: 'Transaction Type' }),
      value: tGlobal(`Transaction.TransactionHistory.transactionTypes.${walletType}`, {
        defaultValue: walletType,
      }),
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: moment(transaction.LastModified).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency || ''} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: `${transaction.Currency || ''} ${formattedCurrency(transaction.HoldAmount)}` || '-',
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
    },
  ];

  const walletDetails = [
    {
      label: getWalletDisplayName(transactionType) + ' ' + t('WalletDetails.token', { defaultValue: 'Token' }),
      value: getWalletToken() || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified || '-',
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.errorMessage', { defaultValue: 'Error Message' }),
      value: state?.failureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={fullName}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        type={
          transactionType === 'GooglePay'
            ? 'Google Pay'
            : transactionType === 'Venmo'
              ? 'Venmo Withdrawal'
              : 'PayPal Withdrawal'
        }
        failureReason={state?.failureReason}
        contactId={state?.accountId}
        jwt={jwt}
        isMainAccount={isMainAccount}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle')}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={walletType}
        jwt={jwt}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('walletDetailsTitle', { walletType: getWalletDisplayName(transactionType) })}
        </Title>
        <DetailsRows className={cn('rows')} details={walletDetails} />
      </Card>

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default DigitalWalletTransactionInfo;
